import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, Building, MapPin, Eye, Search, Filter, Info } from 'lucide-react';
import { useFixedAssets, useFixedAssetsPaginated, useCreateFixedAsset, useUpdateFixedAsset, useDeleteFixedAsset, FixedAsset } from '@/hooks/useFixedAssets';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { FixedAssetForm } from '@/pages/fixedAssets/components/fixed-asset-form/FixedAssetForm';
import { FixedAssetsDistributionChart } from '@/pages/fixedAssets/components/fixed-asset-chart/FixedAssetsDistributionChart';
import type { FixedAssetFormData } from '@/pages/fixedAssets/components/fixed-asset-form/fixedAssetForm.schema';
import { useIsMobile } from '@/hooks/use-mobile';
import { FixedAssetMobileCard } from '@/pages/fixedAssets/components/fixed-asset-mobile-card/FixedAssetMobileCard';

async function uploadOwnershipDocument(
  userId: string,
  assetId: string,
  file: File
): Promise<string> {
  const path = `${userId}/${assetId}/${file.name}`;
  const { error } = await supabase.storage
    .from('ownership-documents')
    .upload(path, file, { upsert: true });
  if (error) throw error;
  const { data } = supabase.storage.from('ownership-documents').getPublicUrl(path);
  return data.publicUrl;
}

async function insertSubTable(assetId: string, data: FixedAssetFormData) {
  const type = data.asset_type;
  if (type === 'apartment') {
    const { error } = await supabase.from('apartments').insert({
      fixed_asset_id: assetId,
      apartment_number: data.apartment_number!,
      expected_rent: data.expected_rent!,
      service_fee: data.service_fee ?? 0,
      management_fee: data.management_fee ?? 0,
      management_fee_vat: data.management_fee_vat ?? 0,
      rent_day_of_month: data.rent_day_of_month!,
      is_occupied: data.is_occupied ?? false,
    });
    if (error) throw error;
  } else if (type === 'land') {
    const { error } = await supabase.from('lands').insert({
      fixed_asset_id: assetId,
      acres: data.acres!,
    });
    if (error) throw error;
  } else if (type === 'house') {
    const { error } = await supabase.from('houses').insert({
      fixed_asset_id: assetId,
      land_id: data.land_id ?? null,
    });
    if (error) throw error;
  } else if (type === 'vehicle') {
    const { error } = await supabase.from('vehicles').insert({ fixed_asset_id: assetId });
    if (error) throw error;
  } else if (type === 'machinery') {
    const { error } = await supabase.from('machinery').insert({ fixed_asset_id: assetId });
    if (error) throw error;
  } else if (type === 'equipment') {
    const { error } = await supabase.from('equipments').insert({ fixed_asset_id: assetId });
    if (error) throw error;
  } else if (type === 'commercial_building') {
    const { error } = await supabase.from('commercial_buildings').insert({ fixed_asset_id: assetId });
    if (error) throw error;
  }
  // 'other' has no sub-table
}

export default function FixedAssetsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<FixedAsset | undefined>();
  const [searchQuery, setSearchQuery] = useState('');
  const [assetTypeFilter, setAssetTypeFilter] = useState('all');
  const createAsset = useCreateFixedAsset();
  const updateAsset = useUpdateFixedAsset();
  const deleteAsset = useDeleteFixedAsset();

  const filters = useMemo(() => ({
    searchQuery: searchQuery || undefined,
    type: assetTypeFilter !== 'all' ? assetTypeFilter : undefined,
  }), [searchQuery, assetTypeFilter]);

  // Hook for total values and chart distribution across all matching items
  const { data: allMatchingAssets, isLoading: totalsLoading } = useFixedAssets(filters);

  // Hook for paginated list
  const { 
    data: paginatedData, 
    fetchNextPage, 
    hasNextPage, 
    isFetchingNextPage, 
    isLoading: assetsLoading 
  } = useFixedAssetsPaginated(filters, 20);

  const flatAssets = useMemo(() => {
    return paginatedData?.pages.flatMap(p => p.assets) || [];
  }, [paginatedData]);

  const fixedAssetsTotal = useMemo(() => {
    if (!allMatchingAssets?.length) return 0;
    return allMatchingAssets.filter(a => a.status === 'owned').reduce((sum, a) => sum + Number(a.value), 0);
  }, [allMatchingAssets]);

  const handleSaveAsset = async (data: FixedAssetFormData, file?: File | null) => {
    try {
      const basePayload = {
        name: data.name,
        asset_type: data.asset_type,
        value: data.value,
        purchase_price: data.purchase_price,
        purchase_date: data.purchase_date || null,
        location: data.location || null,
        currency: data.currency,
        status: data.status ?? 'owned',
        notes: data.notes || null,
        ownership_document_url: data.ownership_document_url || null,
        tenure_type: data.tenure_type || null,
        ownership_status: data.ownership_status ?? 'fully_owned',
        amount_paid: data.ownership_status === 'purchasing' ? (data.amount_paid ?? 0) : data.purchase_price,
      };

      if (editingAsset) {
        // Handle file upload for update
        let docUrl = basePayload.ownership_document_url;
        if (file && user) {
          try {
            docUrl = await uploadOwnershipDocument(user.id, editingAsset.id, file);
          } catch {
            toast.error('Failed to upload ownership document');
            return;
          }
        }
        await updateAsset.mutateAsync({ id: editingAsset.id, ...basePayload, ownership_document_url: docUrl });

        // Update sub-tables if applicable
        if (data.asset_type === 'apartment') {
          const { error: aptError } = await supabase
            .from('apartments')
            .update({
              apartment_number: data.apartment_number!,
              expected_rent: data.expected_rent!,
              service_fee: data.service_fee ?? 0,
              management_fee: data.management_fee ?? 0,
              management_fee_vat: data.management_fee_vat ?? 0,
              rent_day_of_month: data.rent_day_of_month!,
              is_occupied: data.is_occupied ?? false,
            })
            .eq('fixed_asset_id', editingAsset.id);
          if (aptError) {
            toast.error(`Failed to update apartment details: ${aptError.message}`);
            return;
          }
        } else if (data.asset_type === 'land') {
          const { error: landError } = await supabase
            .from('lands')
            .update({ acres: data.acres! })
            .eq('fixed_asset_id', editingAsset.id);
          if (landError) {
            toast.error(`Failed to update land details: ${landError.message}`);
            return;
          }
        } else if (data.asset_type === 'house') {
          const { error: houseError } = await supabase
            .from('houses')
            .update({ land_id: data.land_id ?? null })
            .eq('fixed_asset_id', editingAsset.id);
          if (houseError) {
            toast.error(`Failed to update house details: ${houseError.message}`);
            return;
          }
        }

        toast.success('Asset updated');
      } else {
        // Create the fixed_asset record first
        const newAsset = await createAsset.mutateAsync(basePayload);

        // Handle file upload
        let docUrl = basePayload.ownership_document_url;
        if (file && user) {
          try {
            docUrl = await uploadOwnershipDocument(user.id, newAsset.id, file);
            await updateAsset.mutateAsync({ id: newAsset.id, ownership_document_url: docUrl });
          } catch {
            toast.error('Failed to upload ownership document');
            // Continue — asset was created, just without the document
          }
        }

        // Insert into sub-table
        try {
          await insertSubTable(newAsset.id, data);
        } catch (err: unknown) {
          toast.error(`Asset created but sub-table insert failed: ${(err as Error).message}`);
          return;
        }

        toast.success('Asset added');
        return { id: newAsset.id };
      }

      setAssetDialogOpen(false);
      setEditingAsset(undefined);
    } catch (err: unknown) {
      toast.error((err as Error).message);
    }
  };

  const handleDeleteAsset = async (id: string) => {
    try { await deleteAsset.mutateAsync(id); toast.success('Asset deleted'); }
    catch (err: unknown) { toast.error((err as Error).message); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Fixed & Large Assets</h1>
        <p className="text-muted-foreground">Track your land, properties, vehicles, and other large assets.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Total Fixed Assets Value</div>
            <div className="text-2xl font-bold">${fixedAssetsTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Total Assets</div>
            <div className="text-2xl font-bold">{allMatchingAssets?.filter(a => a.status === 'owned').length ?? 0}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-1">
        {allMatchingAssets && allMatchingAssets.length > 0 && (
          <FixedAssetsDistributionChart fixedAssets={allMatchingAssets} />
        )}
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center flex-1">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search assets by name or location..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Select value={assetTypeFilter} onValueChange={setAssetTypeFilter}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                <SelectValue placeholder="Asset Type" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="land">Land</SelectItem>
              <SelectItem value="house">House</SelectItem>
              <SelectItem value="apartment">Apartment</SelectItem>
              <SelectItem value="commercial_building">Commercial Building</SelectItem>
              <SelectItem value="vehicle">Vehicle</SelectItem>
              <SelectItem value="machinery">Machinery</SelectItem>
              <SelectItem value="equipment">Equipment</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Dialog open={assetDialogOpen} onOpenChange={v => { setAssetDialogOpen(v); if (!v) setEditingAsset(undefined); }}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditingAsset(undefined); setAssetDialogOpen(true); }}>
              <Plus className="mr-2 h-4 w-4" /> Add Asset
            </Button>
          </DialogTrigger>
          <DialogContent className="w-[95vw] sm:w-full sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingAsset ? 'Edit Asset' : 'Add Fixed Asset'}</DialogTitle>
            </DialogHeader>
            <FixedAssetForm
              asset={editingAsset}
              onSave={handleSaveAsset}
              onCancel={() => setAssetDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader><CardTitle>Your Fixed Assets</CardTitle></CardHeader>
        <CardContent>
          {assetsLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : flatAssets.length === 0 ? (
            <p className="text-sm text-muted-foreground">No fixed assets match your search.</p>
          ) : isMobile ? (
            <div className="space-y-3">
              {flatAssets.map(asset => (
                <div key={asset.id} onClick={() => navigate(`/fixed-assets/${asset.id}`)} className="cursor-pointer">
                  <FixedAssetMobileCard
                    asset={asset}
                    onEdit={(a) => { setEditingAsset(a); setAssetDialogOpen(true); }}
                    onDelete={handleDeleteAsset}
                    onView={(a) => navigate(`/fixed-assets/${a.id}`)}
                  />
                </div>
              ))}
              {hasNextPage && (
                <div className="pt-4 flex justify-center">
                  <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                    {isFetchingNextPage ? 'Loading...' : 'Load More'}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-right">Purchase Price</TableHead>
                    <TableHead className="text-right">Current Value</TableHead>
                    <TableHead>Ownership</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[140px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {flatAssets.map(asset => {
                    const appreciation = Number(asset.value) - Number(asset.purchase_price || 0);
                    return (
                      <TableRow 
                        key={asset.id} 
                        className={`cursor-pointer hover:bg-muted/50 ${asset.status === 'sold' ? 'opacity-50' : ''}`}
                        onClick={() => navigate(`/fixed-assets/${asset.id}`)}
                      >
                        <TableCell className="font-medium flex items-center gap-2">
                          <Building className="h-4 w-4 text-muted-foreground" />
                          {asset.name}
                        </TableCell>
                        <TableCell><Badge variant="secondary">{asset.asset_type.replace(/_/g, ' ')}</Badge></TableCell>
                        <TableCell>
                          {asset.location ? (
                            <span className="flex items-center gap-1 text-sm"><MapPin className="h-3 w-3" />{asset.location}</span>
                          ) : '—'}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {Number(asset.purchase_price || 0).toLocaleString(undefined, { style: 'currency', currency: asset.currency })}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {Number(asset.value).toLocaleString(undefined, { style: 'currency', currency: asset.currency })}
                          {asset.purchase_price && appreciation !== 0 && (
                            <div className={`text-xs ${appreciation > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {appreciation > 0 ? '+' : ''}{appreciation.toLocaleString(undefined, { style: 'currency', currency: asset.currency })}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge 
                                  variant={asset.ownership_status === 'fully_owned' ? 'default' : 'secondary'} 
                                  className={asset.ownership_status === 'fully_owned' ? 'bg-emerald-600 hover:bg-emerald-600 cursor-help' : 'bg-blue-100 text-blue-800 hover:bg-blue-100 cursor-help'}
                                >
                                  {asset.ownership_status === 'fully_owned' ? 'Fully Owned' : 'Purchasing'}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{asset.ownership_status === 'fully_owned' ? 'You fully own this asset.' : 'You are currently making payments towards ownership.'}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                        <TableCell>
                          <Badge variant={asset.status === 'owned' ? 'default' : 'outline'}>
                            {asset.status.charAt(0).toUpperCase() + asset.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`View details for ${asset.name}`}
                              onClick={(e) => { e.stopPropagation(); navigate(`/fixed-assets/${asset.id}`); }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={(e) => { e.stopPropagation(); setEditingAsset(asset); setAssetDialogOpen(true); }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <div onClick={(e) => e.stopPropagation()}>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="icon"><Trash2 className="h-4 w-4 text-destructive" /></Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete "{asset.name}"?</AlertDialogTitle>
                                    <AlertDialogDescription>This will permanently remove this asset.</AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDeleteAsset(asset.id)}>Delete</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {hasNextPage && (
                <div className="p-4 flex justify-center">
                  <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                    {isFetchingNextPage ? 'Loading...' : 'Load More'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
